/**
 * Резолвинг роли актора → дашборд (роут).
 *
 * Generic SaaS: роли приходят из JWT/ /v1/platform/me (actor.roles).
 * Приоритет (от высшего доступа к низшему): owner → manager → staff → guest.
 */
import type { ActorRole } from '../api/platform-me.js';

export type DashboardKind = 'owner' | 'manager' | 'staff' | 'guest';

/** Путь дашборда для каждого типа роли. */
export const DASHBOARD_PATH: Readonly<Record<DashboardKind, string>> = {
  owner: '/owner',
  manager: '/manager',
  staff: '/staff',
  guest: '/dashboard',
};

/**
 * Нормализует разные написания ролей бэкенда в DashboardKind.
 * Неизвестные роли игнорируются.
 */
function classify(role: ActorRole): DashboardKind | null {
  switch (role) {
    case 'tenant_owner':
    case 'owner':
      return 'owner';
    case 'hotel_manager':
    case 'manager':
      return 'manager';
    case 'staff':
    case 'front_desk':
    case 'housekeeper':
    case 'maintenance':
    case 'restaurant':
    case 'vendor_buyer':
    case 'vendor':
      return 'staff';
    case 'guest':
      return 'guest';
    default:
      return null;
  }
}

const PRIORITY: readonly DashboardKind[] = ['owner', 'manager', 'staff', 'guest'];

/**
 * Выбирает тип дашборда по набору ролей актора.
 * Берёт роль с наивысшим приоритетом. Если ролей нет/не распознаны —
 * возвращает `guest` как самый ограниченный безопасный дефолт.
 */
export function resolveDashboardKind(roles: readonly ActorRole[]): DashboardKind {
  const kinds = new Set<DashboardKind>();
  for (const r of roles) {
    const k = classify(r);
    if (k) kinds.add(k);
  }
  for (const candidate of PRIORITY) {
    if (kinds.has(candidate)) return candidate;
  }
  return 'guest';
}

/** Возвращает путь дашборда для набора ролей. */
export function resolveDashboardPath(roles: readonly ActorRole[]): string {
  return DASHBOARD_PATH[resolveDashboardKind(roles)];
}
